import { createEffect, createSignal, onCleanup, Show, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { registerPanel } from "@/panel-registry";

interface CaptureResult {
  output?: string;
  content?: string;
  truncated?: boolean;
}

interface ShellStartResult {
  sessionName: string;
  created?: boolean;
  resumed?: boolean;
}

const POLL_MS = 1500;
const DEFAULT_SESSION = "rws-shell";
const ANSI_RE = /\x1b\[[0-9;]*[A-Za-z]/g;

function stripAnsi(value: string): string {
  return value.replace(ANSI_RE, "");
}

const UbuntuTerminalPanel: Component = () => {
  const tools = useToolClient();
  const confirm = useConfirm();
  const [status, setStatus] = createSignal<PanelStatus>({ text: "", level: "info" });
  const [sessionName, setSessionName] = createSignal(DEFAULT_SESSION);
  const [workDir, setWorkDir] = createSignal("/var/lib/remotews");
  const [activeSession, setActiveSession] = createSignal("");
  const [output, setOutput] = createSignal("");
  const [command, setCommand] = createSignal("");
  const [autoRefresh, setAutoRefresh] = createSignal(true);
  const [busy, setBusy] = createSignal(false);
  let timer: ReturnType<typeof setInterval> | null = null;
  let preRef: HTMLPreElement | undefined;

  async function capture(): Promise<void> {
    const name = activeSession();
    if (!name) return;
    try {
      const result = await tools.call<CaptureResult>("cli_session_capture", {
        sessionName: name,
        lines: 3000,
      });
      const text = result.output ?? result.content ?? "";
      setOutput(stripAnsi(text));
      setStatus({
        text: result.truncated ? "Capture tronquee." : `Session ${name}`,
        level: result.truncated ? "warn" : "info",
      });
      queueMicrotask(() => {
        if (preRef) preRef.scrollTop = preRef.scrollHeight;
      });
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    }
  }

  async function startOrResume(): Promise<void> {
    const name = sessionName().trim() || DEFAULT_SESSION;
    if (!activeSession()) {
      const ok = await confirm.confirm({
        title: `Terminal Ubuntu - ${name}`,
        body: `Demarrer ou reprendre une session tmux persistante dans ${workDir().trim() || "/var/lib/remotews"} ?`,
        level: "warn",
        confirmLabel: "Demarrer",
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const result = await tools.call<ShellStartResult>(
        "cli_shell_start",
        {
          sessionName: name,
          workDir: workDir().trim() || "/var/lib/remotews",
        },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActiveSession(result.sessionName);
      setStatus({
        text: result.resumed ? `Session reprise: ${result.sessionName}` : `Session creee: ${result.sessionName}`,
        level: "info",
      });
      await capture();
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function sendCommand(): Promise<void> {
    const name = activeSession();
    const cmd = command();
    if (!name || !cmd.trim() || busy()) return;
    const ok = await confirm.confirm({
      title: `Terminal Ubuntu - ${name}`,
      body: `Envoyer dans la session persistante:\n\n${cmd}`,
      level: "warn",
      confirmLabel: "Envoyer",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await tools.call(
        "cli_session_send",
        { sessionName: name, command: cmd },
        { userConfirmedAt: new Date().toISOString() },
      );
      setCommand("");
      setTimeout(() => void capture(), 250);
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function restartSession(): Promise<void> {
    const name = activeSession();
    if (!name || busy()) return;
    const ok = await confirm.confirm({
      title: `Redemarrer ${name}`,
      body: "La session tmux sera fermee puis recreee avec son contexte sauvegarde.",
      level: "danger",
      confirmLabel: "Redemarrer",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await tools.call("cli_session_restart", { sessionName: name }, { userConfirmedAt: new Date().toISOString() });
      await capture();
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function killSession(): Promise<void> {
    const name = activeSession();
    if (!name || busy()) return;
    const ok = await confirm.confirm({
      title: `Fermer ${name}`,
      body: "La session tmux sera supprimee. Les commandes en cours seront interrompues.",
      level: "danger",
      confirmLabel: "Fermer",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await tools.call("cli_session_kill", { sessionName: name }, { userConfirmedAt: new Date().toISOString() });
      setActiveSession("");
      setOutput("");
      setStatus({ text: "Session fermee.", level: "info" });
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  createEffect(() => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    if (autoRefresh() && activeSession()) {
      timer = setInterval(() => void capture(), POLL_MS);
    }
  });

  onCleanup(() => {
    if (timer) clearInterval(timer);
  });

  const toolbar = (
    <>
      <input
        class="panel-filter-input"
        value={sessionName()}
        onInput={(e) => setSessionName(e.currentTarget.value)}
        disabled={Boolean(activeSession()) || busy()}
        spellcheck={false}
        title="Nom session tmux"
      />
      <input
        class="panel-filter-input"
        value={workDir()}
        onInput={(e) => setWorkDir(e.currentTarget.value)}
        disabled={Boolean(activeSession()) || busy()}
        spellcheck={false}
        title="Dossier de travail"
      />
      <button class="btn btn--sm btn--primary" onClick={() => void startOrResume()} disabled={busy()}>
        {activeSession() ? "Reprendre" : "Demarrer"}
      </button>
      <button class="btn btn--sm" onClick={() => void capture()} disabled={!activeSession()}>
        Actualiser
      </button>
      <label class="panel-filter-checkbox">
        <input type="checkbox" checked={autoRefresh()} onChange={(e) => setAutoRefresh(e.currentTarget.checked)} />
        <span> live</span>
      </label>
    </>
  );

  return (
    <PanelLayout title="Terminal Ubuntu" toolbar={toolbar} status={status()}>
      <div class="ubuntu-terminal">
        <pre ref={preRef} class="ubuntu-terminal__screen">
          <Show when={output()} fallback="Session non initialisee.">
            {output()}
          </Show>
        </pre>
        <div class="ubuntu-terminal__input-row">
          <span class="ubuntu-terminal__prompt">$</span>
          <input
            class="ubuntu-terminal__input"
            value={command()}
            onInput={(e) => setCommand(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void sendCommand();
              }
            }}
            disabled={!activeSession() || busy()}
            spellcheck={false}
            autocomplete="off"
          />
          <button
            class="btn btn--sm btn--primary"
            onClick={() => void sendCommand()}
            disabled={!activeSession() || !command().trim() || busy()}
          >
            Envoyer
          </button>
          <button
            class="btn btn--sm btn--warn"
            onClick={() => void restartSession()}
            disabled={!activeSession() || busy()}
          >
            Restart
          </button>
          <button
            class="btn btn--sm btn--danger"
            onClick={() => void killSession()}
            disabled={!activeSession() || busy()}
          >
            Fermer
          </button>
        </div>
      </div>
      <style>{`
        .ubuntu-terminal { display: flex; flex-direction: column; gap: 10px; min-height: 0; height: 100%; }
        .ubuntu-terminal__screen {
          min-height: 420px;
          max-height: 62vh;
          overflow: auto;
          margin: 0;
          padding: 12px;
          border: 1px solid var(--border, #333);
          background: #07090b;
          color: #d8e2dc;
          font: 12px/1.45 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          white-space: pre-wrap;
          border-radius: 6px;
        }
        .ubuntu-terminal__input-row { display: grid; grid-template-columns: auto minmax(0, 1fr) auto auto auto; gap: 8px; align-items: center; }
        .ubuntu-terminal__prompt { color: var(--accent, #7bd88f); font-weight: 700; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .ubuntu-terminal__input {
          min-width: 0;
          padding: 7px 9px;
          border-radius: 4px;
          border: 1px solid var(--border, #444);
          background: var(--panel-input-bg, #111827);
          color: inherit;
          font: 13px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        }
      `}</style>
    </PanelLayout>
  );
};

registerPanel("ubuntuTerminal", UbuntuTerminalPanel);

export default UbuntuTerminalPanel;
